// ZOC Medical — Marketing content data
// Split out of zocData.js for maintainability.

export const CONTACT_EMAIL = "support@zocmedical.com";
export const BRAND_TAGLINE = "Smarter Care Management. Better Outcomes. Healthier Lives.";

export const PILLARS = [
  {
    key: "platform",
    kicker: "The Platform",
    title: "Software that runs every care program",
    body: "A single system to identify eligible patients, capture consent, build care plans, track time and device data, and generate audit-ready, billing-ready documentation — with compliance built in.",
    href: "/platform",
    points: ["Eligibility & enrollment", "Care plans & risk stratification", "Time, task & device tracking", "Compliance & billing-ready docs"],
  },
  {
    key: "services",
    kicker: "Care Coordination Services",
    title: "A clinical team that delivers the care",
    body: "Our U.S.-based care coordinators work as an extension of your practice — enrolling patients, making monthly check-ins, reviewing monitoring data, and handling documentation so your staff stays focused on the exam room.",
    href: "/services",
    points: ["Patient enrollment & outreach", "Monthly care calls & check-ins", "Monitoring review & escalation", "Documentation done for you"],
  },
  {
    key: "project-management",
    kicker: "Project Management",
    title: "We run the program, your care team runs the care",
    body: "Keep clinical work in-house — ZOC handles the project management backbone. From patient enrollment and workflow design to compliance oversight and clean-claim billing, we make sure every program runs on time, on protocol, and on payment.",
    href: "/project-management",
    points: ["Enrollment planning & rollout", "Workflow & staff training", "Compliance oversight", "Billing & revenue-cycle support"],
  },
];

export const PLATFORM_FEATURES = [
  { title: "Eligibility identification", body: "Surface every patient who qualifies for APCM, RPM, CCM or RTM straight from your EHR data." },
  { title: "Digital consent & enrollment", body: "Compliant, trackable consent capture with cost disclosure — recorded once, referenced forever." },
  { title: "Care plans & risk stratification", body: "Build, share and update electronic care plans and stratify patients by complexity." },
  { title: "Time, task & activity tracking", body: "Every minute and service element logged automatically to meet program minimums." },
  { title: "Device & data integration", body: "Connected-device readings stream in with automatic transmission-day counting and alerts." },
  { title: "Compliance guardrails", body: "Real-time checks flag concurrent-billing conflicts before they ever reach a claim." },
  { title: "Billing-ready documentation", body: "Audit-ready notes and the exact codes to bill, generated for each patient, each month." },
  { title: "Dashboards & reporting", body: "See enrollment, engagement, outcomes and program performance across the whole panel." },
];

export const SERVICES = [
  { n: "01", title: "Patient enrollment & consent", body: "We contact eligible patients, explain the program, secure consent and get them onboarded." },
  { n: "02", title: "Monthly care calls", body: "Dedicated coordinators make the required monthly touchpoints and log every interaction." },
  { n: "03", title: "Remote monitoring review", body: "We watch incoming device data, follow up on readings, and escalate to your clinicians when it matters." },
  { n: "04", title: "Care plan management", body: "We keep each patient's care plan current, coordinated and documented across their conditions." },
  { n: "05", title: "Documentation & time logging", body: "Notes, time logs and service elements are recorded to the standard auditors expect." },
  { n: "06", title: "Device logistics & patient support", body: "Devices shipped, set up and supported — with a friendly line for patient questions." },
];

export const HOW_IT_WORKS = [
  { n: "01", title: "Identify", body: "The platform finds every eligible patient in your panel." },
  { n: "02", title: "Enroll", body: "Our team reaches out, explains the value and captures consent." },
  { n: "03", title: "Engage", body: "Monthly calls, monitoring and coordination keep patients on track." },
  { n: "04", title: "Document", body: "Every touchpoint is logged to a compliant, audit-ready standard." },
  { n: "05", title: "Optimize", body: "Dashboards guide the right program mix for each patient over time." },
];

export const EHRS = [
  "Epic", "Oracle Health (Cerner)", "athenahealth", "eClinicalWorks", "NextGen Healthcare",
  "Veradigm (Allscripts)", "Greenway Health", "MEDITECH", "DrChrono", "Elation Health",
  "Practice Fusion", "Tebra (Kareo)",
];

// Health Information Exchange — talking points (vendor-agnostic)
export const HIE_HIGHLIGHTS = [
  { stat: "Nationwide", label: "Coverage across all major U.S. HIE networks" },
  { stat: "Real-time", label: "ADT alerts the second a patient is admitted or discharged" },
  { stat: "One connection", label: "Every hospital, every discharge, one console" },
  { stat: "HIPAA-secure", label: "Encrypted transport with full audit trail" },
];

export const HIE_CAPABILITIES = [
  {
    title: "Real-time ADT alerts",
    body: "The moment a patient is admitted, discharged or transferred, the hospital's ADT feed lands in your ZOC console — no more finding out weeks later.",
  },
  {
    title: "Automatic TCM workflow",
    body: "Each discharge kicks off a Transition Care Management timeline: 2-day interactive contact, medication reconciliation and a 7/14-day face-to-face — with 99495/99496 documentation pre-filled.",
  },
  {
    title: "Longitudinal record pull",
    body: "Query every major exchange for prior visits, labs, meds and imaging — so your care team sees the whole patient, not just your slice.",
  },
  {
    title: "Reconciled meds & problem list",
    body: "Incoming CCDA and FHIR bundles are parsed, deduplicated and reconciled against your EHR — flagging discrepancies before the face-to-face visit.",
  },
];

// Simulated ADT events for the live ticker (no PHI, illustrative only)
export const HIE_ADT_EVENTS = [
  { patient: "Patient · MRN 4•••291", event: "Discharge · General Hospital", tag: "TCM window open", tone: "#D97706" },
  { patient: "Patient · MRN 8•••104", event: "Admit · Regional Medical Center", tag: "Alert clinician", tone: "#1E5AA8" },
  { patient: "Patient · MRN 2•••673", event: "Discharge · County Health", tag: "2-day contact due", tone: "#D97706" },
  { patient: "Patient · MRN 7•••029", event: "Transfer · SNF placement", tag: "Care plan sync", tone: "#128C8C" },
  { patient: "Patient · MRN 3•••847", event: "ED visit · closed", tag: "Follow-up scheduled", tone: "#4E9E33" },
  { patient: "Patient · MRN 6•••512", event: "Discharge · University Hospital", tag: "Med reconciliation", tone: "#D97706" },
];

export const FAQS = [
  {
    q: "Do we have to use both the software and your care team?",
    a: "No. Use the platform to run care management in-house, have our coordinators run it for you, or combine both. Many practices start with our team and bring more in-house over time.",
  },
  {
    q: "Does this work with our EHR?",
    a: "Yes. The platform is designed to fit alongside your existing EHR and workflows — we confirm the specifics for your system during onboarding.",
  },
  {
    q: "How do patients experience it?",
    a: "As your practice. Our coordinators are trained on your protocols and reach out under your name, with clinical escalations routed straight to your clinicians.",
  },
  {
    q: "How does billing and compliance work?",
    a: "Every program has its own codes, time minimums and documentation. The platform captures audit-ready evidence and flags concurrent-billing conflicts before claims — and our team confirms the right combinations for your patients.",
  },
  {
    q: "How long does it take to launch?",
    a: "We roll out in four phases, starting with a foundation program and layering monitoring, complex care and optimization — so your team builds competency without disruption.",
  },
  {
    q: "Which programs should we start with?",
    a: "Take the 60-second Find Your Fit quiz for a tailored recommendation, or book a demo and we'll map the right mix to your panel.",
  },
];

export const OPPORTUNITY_STATS = [
  { value: "93%", label: "of Medicare patients live with more than one chronic condition that needs continuous care" },
  { value: "<30%", label: "of eligible patients are enrolled in the care they already qualify for" },
  { value: "50%", label: "up to — fewer hospitalizations for patients who are actively managed" },
  { value: "10–15%", label: "lower total cost of care, easing the burden on patients and families" },
  { value: "24/7", label: "access to their care team, not just during office hours" },
  { value: "8", label: "connected Medicare programs that follow each patient's whole journey" },
];

export const MANIFESTO = [
  {
    n: "01",
    title: "Care beyond the visit",
    body: "The office visit is a snapshot. The Care Management Suite makes it a continuous film — proactive contact, monitoring and coordination between appointments, where outcomes are actually decided.",
  },
  {
    n: "02",
    title: "A practice built to keep caring",
    body: "Continuous care only works if your team can sustain it. The Suite fits the work into everyday workflows — so clinicians spend their energy on patients, not paperwork, and the practice can keep showing up.",
  },
  {
    n: "03",
    title: "Compliant by construction",
    body: "Coordinated care must be documented care. Our compatibility guidance, documentation templates and built-in safeguards make compliance the default — protecting patients and the practice alike.",
  },
  {
    n: "04",
    title: "One patient, a lifetime of care",
    body: "The right program follows each patient through enrollment, stabilization, active management, transitions and long-term optimization. One relationship. Every stage.",
  },
];

export const SEGMENTS = [
  { combo: "APCM + RPM", persona: "Complex Medicare · Vital-Sign Monitoring", examples: "Hypertension, diabetes, CHF — BP or glucose monitoring", rpp: "$1,900–$3,100" },
  { combo: "CCM + RTM", persona: "Chronic Conditions · Physical / Respiratory Therapy", examples: "COPD + pulmonary rehab, post-surgical recovery + chronic pain", rpp: "$1,700–$3,300" },
  { combo: "CCM + RPM", persona: "High-Complexity Chronic Disease · Vital Monitoring", examples: "CHF, CKD, diabetes with complications", rpp: "$2,000–$3,400" },
  { combo: "APCM only", persona: "Lower-Complexity Medicare · Preventive Focus", examples: "Preventive care, medication management, care navigation", rpp: "$190–$1,400" },
  { combo: "RTM only", persona: "PT / OT Referred · Therapy-Focused", examples: "Post-surgical MSK rehab, sports injuries, pulmonary rehab", rpp: "$900–$1,600" },
];

export const PM_PHASES = [
  { n: "01", title: "Kickoff & readiness assessment", body: "We audit your panel, EHR configuration and staff capacity — then map the sequence of programs that best fits your practice." },
  { n: "02", title: "Workflow & policy design", body: "We build the enrollment scripts, consent workflows, care-plan templates and documentation SOPs your team will use — customized to how you already work." },
  { n: "03", title: "Staff training & go-live", body: "Role-based training for your care team on the 13 APCM service elements, monitoring review, time-tracking, and audit-ready documentation. Coaching for the first two billing cycles." },
  { n: "04", title: "Compliance oversight", body: "Ongoing chart audits, concurrent-billing checks, and month-end sign-off so nothing goes out the door that wouldn't survive an audit." },
  { n: "05", title: "Billing & revenue-cycle support", body: "Clean claims, denial management and monthly reimbursement reporting — all mapped back to each program (APCM, RPM, CCM, RTM, TCM and preventive)." },
  { n: "06", title: "Continuous optimization", body: "Quarterly performance review, program stacking recommendations, and support as new patients or new programs enter the mix." },
];

export const PM_BENEFITS = [
  { title: "Your clinical relationships stay yours", body: "Patients only ever speak to your team. No outsourced voices, no branding conflicts." },
  { title: "Faster time-to-first-billing", body: "Programs go live in weeks, not quarters — with the compliance guardrails in place from day one." },
  { title: "Predictable compliance", body: "Every consent, care plan, time log and claim is reviewed against CMS rules before it leaves the building." },
  { title: "Revenue you can trust", body: "Clean claims and month-end reimbursement reporting mean fewer denials and no surprises." },
];

export const PHASES = [
  {
    n: "Phase 1",
    title: "Foundation",
    subtitle: "APCM Launch",
    activities: ["EHR patient identification", "Consent workflow rollout", "Care plan templates", "Staff training on 13 service elements"],
    target: "Every Part B patient gets a care plan",
  },
  {
    n: "Phase 2",
    title: "Monitoring Layer",
    subtitle: "Add RPM · Add RTM",
    activities: ["Connected-device fulfillment", "Transmission monitoring", "Real-time alert protocols", "Therapy discipline onboarding"],
    target: "Patients monitored between visits",
  },
  {
    n: "Phase 3",
    title: "Complex Care",
    subtitle: "Launch CCM",
    activities: ["Comprehensive care planning", "24/7 access documentation", "Care-coordination records", "Complex-time capture"],
    target: "24/7 support for high-need patients",
  },
  {
    n: "Phase 4",
    title: "Optimization",
    subtitle: "Full Suite",
    activities: ["Smart patient segmentation", "Compliant program stacking", "Audit-readiness review", "Continuous care review"],
    target: "The right care for every patient",
  },
];

export const CARE_CONTINUUM = ["Enrollment", "Stabilization", "Active Management", "Transitions", "Long-Term Optimization"];

// Calculator scenario blended average monthly revenue per patient
export const SCENARIOS = {
  conservative: { label: "Conservative", blended: 70, description: "Cautious adoption, APCM-led with modest monitoring uptake." },
  balanced: { label: "Balanced", blended: 130, description: "Realistic stacking across a mixed-complexity panel." },
  optimized: { label: "Optimized", blended: 210, description: "Full-suite segmentation with compliant program stacking." },
};
